import { ARRAY, COMPONENT, FUNCTION, NONEXISTENT_OPTIONS, OBJECT } from './constants';

/**
 * Checks if the provided argument is either `undefined` or `null`.
 * Used to determine if a value was not provided.
 * Note: This does NOT treat falsy values (0, false, '', []) as nonexistent,
 * as those are valid values that were explicitly provided.
 *
 * @param {*} argument - The value to check.
 * @returns {boolean} - Returns `true` if the argument is `undefined` or `null`, otherwise `false`.
 */
export function doesNotExist(argument) {
  return NONEXISTENT_OPTIONS.includes(argument);
}

/**
 * Checks whether an argument has been provided (is not undefined or null).
 * This is the inverse of doesNotExist and improves readability when checking for existence.
 * @param {any} argument the argument to verify
 * @returns {boolean} true if the argument exists (is not undefined or null)
 */
export function exists(argument) {
  return !doesNotExist(argument);
}

/**
 * hasCorrectType checks that the provided value has the expected data type
 * @param {*} argument the value to check
 * @param {string} type the data type to validate the argument against
 * @returns {boolean} returns `true` if the argument passes the validation, otherwise `false`
 */
export function hasCorrectType(argument, type) {
  switch (type) {
    case ARRAY:
      return Array.isArray(argument);
    case COMPONENT:
      return isComponentValue(argument);
    default:
      return typeof argument === type;
  }
}

/**
 * Component values arrive in more than one shape. Class-based components and template-only
 * components are functions, while the `component` helper yields an opaque curried value that
 * is only an object. Glimmer exposes no public predicate to tell either apart from an
 * arbitrary value, so accept both shapes rather than rejecting valid invocations.
 * @param {*} argument the value to check
 * @returns {boolean} true when the value could be a component definition
 */
function isComponentValue(argument) {
  return typeof argument === FUNCTION || (exists(argument) && typeof argument === OBJECT);
}

/**
 * Checks whether an argument is active (exists and is not false).
 * This is the inverse of isInactive and improves readability in validation logic.
 * For boolean arguments, only true is considered active.
 * @param {any} argument the argument to verify
 * @returns {boolean} true if the argument is active (exists and is not false)
 */
export function isActive(argument) {
  return !isInactive(argument);
}

/**
 * Checks if the provided argument should be considered "inactive" for validation relationships.
 * For boolean values, `false` is treated as inactive (the argument is not meaningfully active).
 * For string values, empty strings and "initial" are treated as inactive (CSS default/unset state).
 * For all other types, only `undefined` and `null` are considered inactive.
 * Used specifically for `forbidden` and `requires` validations where boolean false
 * or empty/initial strings should not trigger constraint checks.
 *
 * @param {*} argument - The value to check.
 * @returns {boolean} - Returns `true` if the argument is inactive, otherwise `false`.
 */
export function isInactive(argument) {
  return doesNotExist(argument) || isUnset(argument);
}

function isUnset(argument) {
  return [false, '', 'initial'].includes(argument);
}

/**
 * parseArgumentName allows for retrieval of nested values inside of an object for validation
 * if the name is separate by periods, ex: `object.attribute.deeperAttribute`
 * @param {object} args a nested object traversable by the provided name
 * @param {string} name the name to be parsed
 * @returns {*} the value at the terminal access point of the provided name, or undefined
 *   if any intermediate segment is nullish
 */
export function parseArgumentName(args, name) {
  let argument = args;

  for (const segment of name.split('.')) {
    if (doesNotExist(argument)) return undefined;
    argument = argument[segment];
  }

  return argument;
}

/**
 * Checks whether a dot-notation key's parent path resolves to a value.
 * For non-dot-notation keys, always returns true (no parent to check).
 * @param {object} args the arguments object
 * @param {string} name the dot-notation argument name
 * @returns {boolean} true if the parent exists or there is no parent
 */
export function parentExists(args, name) {
  const lastDot = name.lastIndexOf('.');
  if (lastDot === -1) return true;

  return exists(parseArgumentName(args, name.substring(0, lastDot)));
}

export default {
  doesNotExist,
  exists,
  hasCorrectType,
  isActive,
  isInactive,
  parentExists,
  parseArgumentName,
};
